import { withOktaAuth } from "@okta/okta-react";
import { AuthState } from "@okta/okta-auth-js";
import * as React from "react";
import { connect } from "react-redux";
import { compose, Dispatch } from "redux";
import { Routes } from "../../AppConstants";
import { validateAuthState } from "../../Auth/AuthHelper";
import i18n from "../../i18n";
import { IStoreState } from "../../Model";
import { Error } from "../../Shared/Error";
import { LoadingIndicator } from "../../Shared/LoadingIndicator";
import DeletedResponseList from "../../Shared/ResponseTable/DeletedResponseList";
import { ResetResponseVersionsAction } from "../../Responses/duck/Actions";
import {
  getDeletedResponseListThunk,
  setDeletedSortOrder,
  resetDeletedSortOrder,
  setDeletedFilter,
  clearDeletedFilter,
  clearAllDeletedFilters,
} from "../duck/Actions";
import { IDeletedRequestParams, fetchDeletedResponsesFacet } from "../duck/Operations";
import { IFilter } from "../../Responses/duck/Models";
import { WithOktaAuthProps } from "../../Auth/duck/Models";

export interface IDeletedResponsesStateProps {
  failed: boolean;
  fetched: boolean;
  fetching: boolean;
  deletedResponses: Api.IPagedResult<Api.IDeletedResponseListViewModel>;
  orderBy: string;
  filters: IFilter[];
  filteredResults: Api.IDeletedResponseListViewModel[];
}

interface IDeletedResponsesPageDispatchProps {
  fetchDeletedResponseList: (auth: AuthState | null, requestParams: IDeletedRequestParams) => any;
  setSortOrder: (orderBy: string) => void;
  resetSortOrder: () => void;
  setFilter: (field: string, value: any, operator?: string) => void;
  clearFilter: (field: string) => void;
  clearAllFilters: () => void;
  resetVersionHistory: () => void;
}

interface IDeletedResponsesPageOwnProps {
  currentPage: number;
}

type IDeletedResponsesProps = IDeletedResponsesStateProps &
  IDeletedResponsesPageDispatchProps &
  IDeletedResponsesPageOwnProps &
  WithOktaAuthProps;

class DeletedListPage extends React.Component<IDeletedResponsesProps> {
  private buildRequestParams(): IDeletedRequestParams {
    return {
      pageNumber: this.props.currentPage,
      pageSize: this.props.deletedResponses.pageSize,
      orderBy: this.props.orderBy,
      filter: { filters: this.props.filters }
    };
  }

  public componentDidMount() {
    if (this.props.authState?.isAuthenticated) {
      this.props.fetchDeletedResponseList(this.props.authState, this.buildRequestParams());
    } else {
      validateAuthState(this.props.authState);
    }
  }

  public componentDidUpdate(prevProps: IDeletedResponsesProps) {
    if (this.props.authState?.isAuthenticated) {
      if (this.props.fetching) {
        return;
      }
      if (
        prevProps.currentPage !== this.props.currentPage ||
        prevProps.orderBy !== this.props.orderBy ||
        prevProps.filters !== this.props.filters
      ) {
        this.props.fetchDeletedResponseList(this.props.authState, this.buildRequestParams());
      }
    } else {
      validateAuthState(this.props.authState);
    }
  }

  public componentWillUnmount() {
    this.props.resetSortOrder();
    this.props.clearAllFilters();
  }

  public getFacetsData = (propertyName: string) => {
    return fetchDeletedResponsesFacet(this.props.authState, propertyName)
      .then((response: any) => response)
      .catch((errorResponse: any) => { console.error(errorResponse); });
  }

  public handleSortChange = (orderBy: string) => {
    this.props.setSortOrder(orderBy);
  }

  public handlePageSizeChange = (pageNumber: number, itemPerPage: number, auth: AuthState | null) => {
    if (auth) {
      const requestParams: IDeletedRequestParams = {
        pageNumber,
        pageSize: itemPerPage,
        orderBy: this.props.orderBy,
        filter: { filters: this.props.filters }
      };
      this.props.fetchDeletedResponseList(auth, requestParams);
    }
  }

  public render() {
    if (!this.props.authState) {
      return <LoadingIndicator busy={true} />;
    }
    if (this.props.authState?.error) {
      throw this.props.authState.error;
    }
    return (
      <React.Fragment>
        <h1 className="title">{i18n.t("deletedResponseList.heading")}</h1>
        {this.props.failed && (
          <div className="error-container">
            <Error message={i18n.t("deletedResponseList.fetchingDeletedResponsesFailed")} />
          </div>
        )}
        <LoadingIndicator busy={this.props.fetching} />
        {this.props.fetched && (
          <DeletedResponseList
            list={this.props.deletedResponses}
            baseRoute={Routes.deletedResponseList}
            handlePageSizeChange={this.handlePageSizeChange}
            authState={this.props.authState}
            onSortChange={this.handleSortChange}
            orderBy={this.props.orderBy}
            filters={this.props.filters}
            filteredResults={this.props.filteredResults}
            setFilter={this.props.setFilter}
            clearFilter={this.props.clearFilter}
            clearAllFilters={this.props.clearAllFilters}
            onFacetChange={this.getFacetsData}
            resetVersionHistory={this.props.resetVersionHistory}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: IStoreState) => {
  return {
    ...state.deletedResponses
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchDeletedResponseList: (auth: AuthState, requestParams: IDeletedRequestParams) => {
      dispatch(getDeletedResponseListThunk(auth, requestParams));
    },
    setSortOrder: (orderBy: string) => {
      dispatch(setDeletedSortOrder(orderBy));
    },
    resetSortOrder: () => {
      dispatch(resetDeletedSortOrder());
    },
    setFilter: (field: string, value: any, operator?: string) => {
      dispatch(setDeletedFilter(field, value, operator));
    },
    clearFilter: (field: string) => {
      dispatch(clearDeletedFilter(field));
    },
    clearAllFilters: () => {
      dispatch(clearAllDeletedFilters());
    },
    resetVersionHistory: () => {
      dispatch(new ResetResponseVersionsAction());
    }
  };
};

const hoc = compose(
  withOktaAuth,
  connect<
    IDeletedResponsesStateProps,
    IDeletedResponsesPageDispatchProps,
    IDeletedResponsesPageOwnProps
  >(mapStateToProps, mapDispatchToProps)
)(DeletedListPage);

export { hoc as DeletedListPage };
